import { useEffect, useMemo, useRef, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { getAllProducts, addToCart, DEFAULT_CATEGORIES } from '../../services';
import { useAuth } from '../../auth/AuthContext';
import { useTranslation } from 'react-i18next';
import { getEffectivePrice, hasActivePromotion, getDiscountPercentage } from '../../utils/promotionHelper';
import styles from './CustomerDashboard.module.css';

export default function CustomerDashboard() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const outletCtx = useOutletContext();
  const searchQuery = outletCtx?.searchQuery || '';
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [categoryOpen, setCategoryOpen] = useState(false);
  const categoryRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  // Variant selection modal
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [quantity, setQuantity] = useState(1);
  const [addingToCart, setAddingToCart] = useState(false);

  // Promotion slider
  const [currentPromoIndex, setCurrentPromoIndex] = useState(0);

  // Auto slide promotion banner every 5 seconds
  useEffect(() => {
    const promoProducts = products.filter(p => hasActivePromotion(p));
    if (promoProducts.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentPromoIndex((prev) => (prev + 1) % promoProducts.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [products]);

  useEffect(() => {
    const loadProducts = async () => {
      try {
        const productsData = await getAllProducts();
        console.log('Loaded products:', productsData);
        
        // Check promotion data
        productsData.forEach(p => {
          if (p.productName?.includes('หูฟัง')) {
            console.log('หูฟัง product:', p);
            console.log('Has promotion?', p.promotion);
            console.log('Is active?', hasActivePromotion(p));
          }
        });
        
        // Filter only products that still have available stock for customers
        const available = productsData.filter(p => {
          const qty = parseInt(p.quantity || 0);
          const reserved = parseInt(p.reserved || 0);
          const staffReserved = parseInt(p.staffReserved || 0);
          const availableForCustomer = qty - reserved - staffReserved;
          return availableForCustomer > 0;
        });
        setProducts(available);
      } catch (error) {
        console.error('Error loading products:', error);
      } finally {
        setLoading(false);
      }
    };
    loadProducts();
  }, []);

  const uniqueCategories = useMemo(() => {
    const cats = products.map(p => p.category).filter(c => c && c.trim() !== '');
    const sorted = [...new Set([...DEFAULT_CATEGORIES, ...cats])].sort();

    const otherLabel = t('product.other');
    const idx = sorted.findIndex((c) => String(c).trim() === otherLabel);
    if (idx >= 0) {
      const [other] = sorted.splice(idx, 1);
      sorted.push(other);
    }

    return sorted;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products]);

  useEffect(() => {
    if (!categoryOpen) return;
    const onDown = (e) => {
      if (!categoryRef.current) return;
      if (!categoryRef.current.contains(e.target)) {
        setCategoryOpen(false);
      }
    };
    window.addEventListener('mousedown', onDown);
    return () => window.removeEventListener('mousedown', onDown);
  }, [categoryOpen]);

  const filteredProducts = useMemo(() => {
    let filtered = products;
    if (searchQuery.trim() !== '') {
      filtered = filtered.filter(product =>
        product.productName?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    if (categoryFilter) {
      filtered = filtered.filter(product => product.category === categoryFilter);
    }
    return filtered;
  }, [products, searchQuery, categoryFilter]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, categoryFilter]);

  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentProducts = filteredProducts.slice(startIndex, startIndex + itemsPerPage);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const openProductModal = (product) => {
    setSelectedProduct(product);
    setSelectedVariant(null);
    setSelectedOptions({});
    setQuantity(1);
  };

  const closeModal = () => {
    setSelectedProduct(null);
    setSelectedVariant(null);
    setSelectedOptions({});
    setQuantity(1);
  };

  const handleAddToCart = async () => {
    if (!user) {
      alert(t('cart.please_login_cart'));
      return;
    }

    const product = selectedProduct;
    if (!product) return;

    const hasVariants = product.hasVariants && Array.isArray(product.variants) && product.variants.length > 0;

    if (hasVariants && !selectedVariant) {
      alert(t('cart.please_select_variant'));
      return;
    }

    setAddingToCart(true);
    try {
      const cartItem = {
        productId: product.id,
        productName: product.productName,
        image: product.image,
        unit: product.unit || t('common.piece'),
        quantity: quantity,
      };

      const selectedOptionsPayload =
        selectedOptions && typeof selectedOptions === 'object'
          ? Object.fromEntries(
              Object.entries(selectedOptions)
                .map(([k, v]) => [k, String(v || '').trim()])
                .filter(([k, v]) => k && v)
            )
          : null;

      if (selectedOptionsPayload && Object.keys(selectedOptionsPayload).length > 0) {
        cartItem.selectedOptions = selectedOptionsPayload;
      }

      if (hasVariants && selectedVariant) {
        cartItem.variantSize = selectedVariant.size;
        cartItem.variantColor = selectedVariant.color;
        cartItem.sellPrice = selectedVariant.sellPrice;
        const vQty = parseInt(selectedVariant.quantity || 0);
        const vReserved = parseInt(selectedVariant.reserved || 0);
        const vStaffReserved = parseInt(selectedVariant.staffReserved || 0);
        cartItem.maxQuantity = Math.max(0, vQty - vReserved - vStaffReserved);
      } else {
        // Use promotion price if active
        cartItem.sellPrice = getEffectivePrice(product);
        const pQty = parseInt(product.quantity || 0);
        const pReserved = parseInt(product.reserved || 0);
        const pStaffReserved = parseInt(product.staffReserved || 0);
        cartItem.maxQuantity = Math.max(0, pQty - pReserved - pStaffReserved);
      }

      await addToCart(user.uid, cartItem);
      window.dispatchEvent(new Event('customer-cart-updated'));
      closeModal();
    } catch (error) {
      console.error('Error adding to cart:', error);
      alert(t('common.error') + ': ' + error.message);
    } finally {
      setAddingToCart(false);
    }
  };

  const getAvailableQuantity = () => {
    if (!selectedProduct) return 0;
    const hasVariants = selectedProduct.hasVariants && Array.isArray(selectedProduct.variants);
    if (hasVariants && selectedVariant) {
      const vQty = parseInt(selectedVariant.quantity || 0);
      const vReserved = parseInt(selectedVariant.reserved || 0);
      const vStaffReserved = parseInt(selectedVariant.staffReserved || 0);
      return Math.max(0, vQty - vReserved - vStaffReserved);
    }
    const pQty = parseInt(selectedProduct.quantity || 0);
    const pReserved = parseInt(selectedProduct.reserved || 0);
    const pStaffReserved = parseInt(selectedProduct.staffReserved || 0);
    return Math.max(0, pQty - pReserved - pStaffReserved);
  };

  const getDisplayPrice = (product) => {
    const hasVariants = product.hasVariants && Array.isArray(product.variants) && product.variants.length > 0;
    if (hasVariants) {
      const prices = product.variants.map(v => v.sellPrice || 0);
      const min = Math.min(...prices);
      const max = Math.max(...prices);
      return min === max ? `฿${min.toLocaleString()}` : `฿${min.toLocaleString()} - ฿${max.toLocaleString()}`;
    }
    return `฿${(product.sellPrice || product.price || 0).toLocaleString()}`;
  };

  const promoProducts = products.filter(p => hasActivePromotion(p));

  return (
    <div>
      {/* Promo Banner */}
      {promoProducts.length > 0 && (() => {
        const featuredPromo = promoProducts[currentPromoIndex % promoProducts.length];
        const discount = getDiscountPercentage(featuredPromo);
        const effectivePrice = getEffectivePrice(featuredPromo);
        const originalPrice = featuredPromo.sellPrice || featuredPromo.price || 0;

        return (
          <div 
            className={styles.promoBanner}
            onClick={() => openProductModal(featuredPromo)}
            style={{ cursor: 'pointer' }}
          >
            <div className={styles.promoBannerInner}>
              <div 
                className={styles.promoBannerImage}
                style={{ backgroundImage: featuredPromo.image ? `url('${featuredPromo.image}')` : 'none' }}
              >
                <div className={styles.promoBannerImageOverlay} />
              </div>
              <div className={styles.promoBannerContent}>
                <div className={styles.promoBadge}>
                  <span className={`material-symbols-outlined ${styles.promoBadgeIcon}`}>bolt</span>
                  <span className={styles.promoBadgeText}>🔥 {t('product.sale')} {discount}% {t('product.off')}</span>
                </div>
                <h2 className={styles.promoTitle}>{featuredPromo.productName}</h2>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <span style={{ fontSize: '1.5rem', fontWeight: 700, color: '#135bec' }}>
                    ฿{effectivePrice.toLocaleString()}
                  </span>
                  <span style={{ fontSize: '1rem', color: '#9ca3af', textDecoration: 'line-through' }}>
                    ฿{originalPrice.toLocaleString()}
                  </span>
                </div>
                <p className={styles.promoDescription}>
                  {featuredPromo.description || t('product.special_promotion')}
                </p>
                <button className={styles.promoButton}>
                  {t('product.buy_now')}
                  <span className="material-symbols-outlined" style={{ fontSize: '1.125rem' }}>arrow_forward</span>
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Section Header */}
      <div className={styles.sectionHeader} style={{ marginTop: promoProducts.length > 0 ? '1.5rem' : 0 }}>
        <h1 className={styles.sectionTitle}>{t('product.all_products') || 'Featured Products'}</h1>
        <div className={styles.sectionActions}>
          <button className={styles.filterButton}>
            <span className="material-symbols-outlined" style={{ fontSize: '1.25rem' }}>filter_list</span>
            {t('common.filters') || 'Filters'}
          </button>
          <div className={styles.categoryDropdown} ref={categoryRef}>
            <button
              type="button"
              className={styles.categoryButton}
              onClick={() => setCategoryOpen((v) => !v)}
              aria-haspopup="listbox"
              aria-expanded={categoryOpen}
            >
              <span>
                {categoryFilter
                  ? t(`categories.${categoryFilter}`, categoryFilter)
                  : (t('common.all_categories') || 'All Categories')}
              </span>
              <span className="material-symbols-outlined" style={{ fontSize: '1.25rem' }}>keyboard_arrow_down</span>
            </button>

            {categoryOpen && (
              <div className={styles.categoryMenu} role="listbox">
                <button
                  type="button"
                  className={styles.categoryMenuItem}
                  onClick={() => {
                    setCategoryFilter('');
                    setCurrentPage(1);
                    setCategoryOpen(false);
                  }}
                >
                  {t('common.all_categories') || 'All Categories'}
                </button>
                {uniqueCategories.map((cat) => (
                  <button
                    key={cat}
                    type="button"
                    className={styles.categoryMenuItem}
                    onClick={() => {
                      setCategoryFilter(cat);
                      setCurrentPage(1);
                      setCategoryOpen(false);
                    }}
                  >
                    {t(`categories.${cat}`, cat)}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Filter Tags */}
      {categoryFilter && (
        <div className={styles.filterTags}>
          <button className={`${styles.filterTag} ${styles.filterTagActive}`}>
            {categoryFilter}
            <span 
              className="material-symbols-outlined" 
              style={{ fontSize: '1rem' }}
              onClick={(e) => {
                e.stopPropagation();
                setCategoryFilter('');
              }}
            >
              close
            </span>
          </button>
        </div>
      )}

      {/* Products Grid */}
      {loading ? (
        <div className={styles.loadingState}>
          <p className={styles.loadingText}>{t('common.loading')}</p>
        </div>
      ) : currentProducts.length === 0 ? (
        <div className={styles.emptyState}>
          <p className={styles.emptyText}>
            {searchQuery ? t('product.no_products_found') : t('product.no_products')}
          </p>
        </div>
      ) : (
        <div className={styles.productsGrid}>
          {currentProducts.map((product) => {
            const hasVariants = product.hasVariants && Array.isArray(product.variants) && product.variants.length > 0;
            const qty = parseInt(product.quantity || 0);
            const reserved = parseInt(product.reserved || 0);
            const staffReserved = parseInt(product.staffReserved || 0);
            const availableForCustomer = Math.max(0, qty - reserved - staffReserved);
            const isOutOfStock = availableForCustomer <= 0;
            const isLowStock = availableForCustomer > 0 && availableForCustomer <= 5;
            const isPromo = hasActivePromotion(product);

            return (
              <div
                key={product.id}
                className={`${styles.productCard} ${styles.productCardClickable} ${isOutOfStock ? styles.productCardOutOfStock : ''}`}
                role="button"
                tabIndex={0}
                onClick={() => openProductModal(product)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    openProductModal(product);
                  }
                }}
              >
                {/* Image */}
                <div className={styles.productImageWrapper}>
                  {isPromo && (
                    <span className={`${styles.productBadge} ${styles.productBadgeSale}`}>
                      -{getDiscountPercentage(product)}%
                    </span>
                  )}
                  
                  {isOutOfStock && (
                    <div className={styles.outOfStockOverlay}>
                      <span className={styles.outOfStockBadge}>{t('product.out_of_stock') || 'Out of Stock'}</span>
                    </div>
                  )}
                  
                  <div
                    className={`${styles.productImage} ${isOutOfStock ? styles.productImageGrayscale : ''}`}
                    style={{ 
                      backgroundImage: product.image ? `url('${product.image}')` : 'none',
                      backgroundColor: !product.image ? '#f3f4f6' : undefined
                    }}
                  />
                </div>

                {/* Info */}
                <div className={styles.productInfo}>
                  <div className={styles.productCategory}>{product.category || 'General'}</div>
                  <h3 className={styles.productName}>
                    {product.productName}
                    {hasVariants && (
                      <span className={styles.productNameThai}>
                        ({product.variants.length} {t('product.variants') || 'variants'})
                      </span>
                    )}
                  </h3>
                  
                  {/* Description */}
                  {product.description && (
                    <p className={styles.productDescription}>
                      {product.description}
                    </p>
                  )}
                  
                  <div className={styles.productPriceRow}>
                    <span className={styles.productPrice}>
                      {isPromo ? `฿${getEffectivePrice(product).toLocaleString()}` : getDisplayPrice(product)}
                    </span>
                    {isPromo && !hasVariants && (
                      <span className={styles.productPriceOriginal}>
                        ฿{(product.sellPrice || product.price || 0).toLocaleString()}
                      </span>
                    )}
                  </div>
                  
                  <div className={`${styles.productStock} ${
                    isOutOfStock ? styles.productStockOutOfStock : 
                    isLowStock ? styles.productStockLowStock : 
                    styles.productStockInStock
                  }`}>
                    <span className="material-symbols-outlined" style={{ fontSize: '0.875rem' }}>
                      {isOutOfStock ? 'cancel' : isLowStock ? 'warning' : 'check_circle'}
                    </span>
                    {isOutOfStock 
                      ? (t('product.out_of_stock') || 'Out of Stock')
                      : isLowStock 
                        ? `${t('product.low_stock') || 'Low Stock'} (${availableForCustomer} ${product.unit || t('common.piece') || 'units'})`
                        : `${t('product.in_stock') || 'In Stock'} (${availableForCustomer} ${product.unit || t('common.piece') || 'units'})`
                    }
                  </div>
                  
                  {isOutOfStock ? (
                    <button className={styles.notifyMeButton} disabled>
                      <span className="material-symbols-outlined" style={{ fontSize: '1.125rem' }}>notifications</span>
                      {t('product.notify_me') || 'Notify Me'}
                    </button>
                  ) : (
                    <button 
                      className={styles.addToCartButton}
                      onClick={(e) => {
                        e.stopPropagation();
                        openProductModal(product);
                      }}
                    >
                      <span className="material-symbols-outlined" style={{ fontSize: '1.125rem' }}>add_shopping_cart</span>
                      {t('cart.add_to_cart') || 'Add to Cart'}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className={styles.pagination}>
          <button
            className={styles.paginationButton}
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            {t('common.previous') || 'Previous'}
          </button>
          <span style={{ padding: '0.5rem 1rem', fontSize: '0.875rem', color: '#374151' }}>
            {t('common.page') || 'Page'} {currentPage} / {totalPages}
          </span>
          <button
            className={styles.paginationButton}
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            {t('common.next') || 'Next'}
          </button>
        </div>
      )}


      {/* Variant Selection Modal */}
      {selectedProduct && (
        <div className={styles.modalOverlay} onClick={closeModal}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className={styles.modalHeader}>
              <h2 className={styles.modalTitle}>{selectedProduct.productName}</h2>
              <button className={styles.modalCloseButton} onClick={closeModal}>×</button>
            </div>

            {/* Modal Body */}
            <div className={styles.modalBody}>
              {/* Product Image */}
              <div className={styles.modalProductImage}>
                {selectedProduct.image ? (
                  <img src={selectedProduct.image} alt={selectedProduct.productName} />
                ) : (
                  <span className="material-symbols-outlined" style={{ fontSize: 60, color: '#9ca3af' }}>
                    inventory_2
                  </span>
                )}
              </div>

              {selectedProduct.description && (
                <div
                  style={{
                    marginBottom: '1.25rem',
                    padding: '0.875rem 1rem',
                    background: '#ffffff',
                    borderRadius: '0.75rem',
                    border: '1px solid #e5e7eb',
                  }}
                >
                  <div style={{ fontSize: '0.8125rem', fontWeight: 700, color: '#111827', marginBottom: '0.375rem' }}>
                    {t('product.description')}
                  </div>
                  <div
                    style={{
                      fontSize: '0.8125rem',
                      color: '#374151',
                      lineHeight: 1.6,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {selectedProduct.description}
                  </div>
                </div>
              )}

              {/* Variant Selection */}
              {selectedProduct.hasVariants && Array.isArray(selectedProduct.variants) && selectedProduct.variants.length > 0 ? (
                <div style={{ marginBottom: '1.25rem' }}>
                  <h3 style={{ margin: '0 0 0.75rem', fontSize: '0.875rem', fontWeight: 600, color: '#374151' }}>
                    {t('product.select_variant') || 'Select Variant'}:
                  </h3>
                  <div className={styles.variantGrid}>
                    {selectedProduct.variants.map((variant, idx) => {
                      const vQty = parseInt(variant.quantity || 0);
                      const vReserved = parseInt(variant.reserved || 0);
                      const vStaffReserved = parseInt(variant.staffReserved || 0);
                      const available = Math.max(0, vQty - vReserved - vStaffReserved);
                      const isSelected = selectedVariant === variant;
                      const isVarOutOfStock = available <= 0;
                      
                      return (
                        <button
                          key={idx}
                          onClick={() => !isVarOutOfStock && setSelectedVariant(variant)}
                          disabled={isVarOutOfStock}
                          className={`${styles.variantButton} ${isSelected ? styles.variantButtonSelected : ''}`}
                        >
                          <div style={{ fontWeight: 600, fontSize: '0.8125rem', color: isSelected ? '#1e40af' : '#111827' }}>
                            {variant.size}
                          </div>
                          <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.125rem' }}>
                            {variant.color}
                          </div>
                          <div style={{ fontSize: '0.875rem', fontWeight: 700, color: '#111827', marginTop: '0.375rem' }}>
                            ฿{(variant.sellPrice || 0).toLocaleString()}
                          </div>
                          <div style={{ fontSize: '0.625rem', color: isVarOutOfStock ? '#ef4444' : '#6b7280', marginTop: '0.25rem' }}>
                            {isVarOutOfStock ? (t('product.out_of_stock') || 'Out of Stock') : `${t('product.remaining') || 'Remaining'} ${available}`}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className={styles.priceInfoBox}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '0.875rem', color: '#374151' }}>{t('common.price') || 'Price'}:</span>
                    <span style={{ fontSize: '1.25rem', fontWeight: 700, color: '#111827' }}>
                      ฿{getEffectivePrice(selectedProduct).toLocaleString()}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.5rem' }}>
                    <span style={{ fontSize: '0.8125rem', color: '#6b7280' }}>{t('product.remaining') || 'Remaining'}:</span>
                    <span style={{ fontSize: '0.8125rem', color: '#6b7280' }}>
                      {getAvailableQuantity()} {selectedProduct.unit || t('common.piece') || 'units'}
                    </span>
                  </div>
                </div>
              )}

              {/* Selected Variant Info */}
              {selectedVariant && (
                <div className={styles.selectedVariantBox}>
                  <div style={{ fontSize: '0.8125rem', color: '#1e40af', fontWeight: 600, marginBottom: '0.5rem' }}>
                    {t('common.selected') || 'Selected'}:
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '0.875rem', color: '#374151' }}>
                      {selectedVariant.size} / {selectedVariant.color}
                    </span>
                    <span style={{ fontSize: '1.125rem', fontWeight: 700, color: '#111827' }}>
                      ฿{(selectedVariant.sellPrice || 0).toLocaleString()}
                    </span>
                  </div>
                </div>
              )}

              {selectedProduct.variationOptions && typeof selectedProduct.variationOptions === 'object' && (
                (() => {
                  const labelMap = {
                    ram: 'variation_ram',
                    storage: 'variation_storage',
                    color: 'variation_color',
                    screenSize: 'variation_screen_size',
                    condition: 'variation_condition',
                    fitStyle: 'variation_fit_style',
                    length: 'variation_length',
                    pattern: 'variation_pattern',
                    gender: 'variation_gender',
                    flavor: 'variation_flavor',
                    sizeWeight: 'variation_size_weight',
                    volume: 'variation_volume',
                    packageType: 'variation_package_type',
                    shadeTone: 'variation_shade_tone',
                    volumeSize: 'variation_volume_size',
                    skinType: 'variation_skin_type',
                    spf: 'variation_spf',
                    size: 'variation_size',
                    tipSize: 'variation_tip_size',
                    packSize: 'variation_pack_size',
                    material: 'variation_material',
                    voltagePower: 'variation_voltage_power',
                    type: 'variation_type',
                    capacity: 'variation_capacity',
                    powerWattage: 'variation_power_wattage',
                    oemAftermarket: 'variation_oem_aftermarket',
                    sizeDimension: 'variation_size_dimension',
                    level: 'variation_level',
                    ageGroup: 'variation_age_group',
                    ageRange: 'variation_age_range',
                    batteryRequired: 'variation_battery_required',
                    batteryType: 'variation_battery_type',
                  };

                  const entries = Object.entries(selectedProduct.variationOptions)
                    .filter(([, arr]) => Array.isArray(arr) && arr.length > 0)
                    .map(([k, arr]) => [k, arr.map((x) => String(x)).filter(Boolean)]);

                  if (entries.length === 0) return null;

                  return (
                    <div style={{ marginBottom: '1.25rem' }}>
                      <h3 style={{ margin: '0 0 0.75rem', fontSize: '0.875rem', fontWeight: 600, color: '#374151' }}>
                        {t('product.variation_options') || 'Options'}:
                      </h3>

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 10 }}>
                        {entries.map(([key, options]) => (
                          <div key={key}>
                            <label style={{ display: 'block', marginBottom: 6, fontSize: '0.8125rem', fontWeight: 600, color: '#374151' }}>
                              {t(`product.${labelMap[key] || ''}`, key)}
                            </label>
                            <select
                              value={selectedOptions[key] || ''}
                              onChange={(e) =>
                                setSelectedOptions((prev) => ({
                                  ...prev,
                                  [key]: e.target.value,
                                }))
                              }
                              className={styles.quantityInput}
                              style={{ width: '100%' }}
                            >
                              <option value="">-</option>
                              {options.map((opt) => (
                                <option key={opt} value={opt}>
                                  {opt}
                                </option>
                              ))}
                            </select>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()
              )}

              {/* Quantity */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 600, color: '#374151' }}>
                  {t('common.quantity') || 'Quantity'}:
                </label>
                <div className={styles.quantityControl}>
                  <button 
                    className={styles.quantityButton}
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  >
                    -
                  </button>
                  <input 
                    type="number" 
                    value={quantity} 
                    onChange={(e) => setQuantity(Math.max(1, Math.min(getAvailableQuantity(), parseInt(e.target.value) || 1)))} 
                    min="1" 
                    max={getAvailableQuantity()} 
                    className={styles.quantityInput}
                  />
                  <button 
                    className={styles.quantityButton}
                    onClick={() => setQuantity(Math.min(getAvailableQuantity(), quantity + 1))}
                  >
                    +
                  </button>
                  <span style={{ fontSize: '0.8125rem', color: '#6b7280' }}>
                    / {getAvailableQuantity()} {selectedProduct.unit || t('common.piece') || 'units'}
                  </span>
                </div>
              </div>

              {/* Add to Cart Button */}
              <button
                onClick={handleAddToCart}
                disabled={addingToCart || (selectedProduct.hasVariants && !selectedVariant) || getAvailableQuantity() <= 0}
                className={styles.addToCartModalButton}
              >
                {addingToCart 
                  ? (t('cart.adding_to_cart') || 'Adding...') 
                  : getAvailableQuantity() <= 0 
                    ? (t('product.out_of_stock') || 'Out of Stock') 
                    : `${t('cart.add_to_cart') || 'Add to Cart'} (฿${((selectedVariant?.sellPrice || getEffectivePrice(selectedProduct)) * quantity).toLocaleString()})`
                }
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
